import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, Dropdown, Empty, Page, Table, toneForStatus } from '../components/ui.jsx';
import { FlowDiagram } from '../features/live/FlowDiagram.jsx';
import { useTraffic } from '../hooks/useTraffic.js';
import { trafficStore } from '../stores/traffic.js';
import { formatDuration, formatTime } from '../utils/format.js';

/**
 * The traffic as it happens, unfiltered by default. Everything on this page comes from the live
 * stream: no request is made while it is open, and the filters work on what the browser already
 * holds, so changing one is instant.
 */
export function LiveTraffic() {
  const traffic = useTraffic();
  const [channel, setChannel] = useState('');
  const [application, setApplication] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(false);

  const channels = useMemo(() => [...new Set(traffic.events.map((event) => event.channel).filter(Boolean))].sort() as string[], [traffic.events]);
  const applications = useMemo(
    () => [...new Set(traffic.events.flatMap((event) => [event.from, event.to]).filter(Boolean))].sort() as string[],
    [traffic.events],
  );

  const shown = traffic.events.filter((event) => {
    if (channel && event.channel !== channel) return false;
    if (application && event.from !== application && event.to !== application) return false;
    if (onlyProblems && !['message.failed', 'message.dead-lettered', 'message.retried'].includes(event.type)) return false;
    return true;
  });

  return (
    <Page
      eyebrow="OSSERVA"
      title="Live Traffic"
      description="Ogni messaggio mentre attraversa Broker. La connessione è una sola, condivisa da tutta la console."
      actions={
        <>
          <Badge tone={traffic.connection === 'live' ? 'good' : traffic.connection === 'connecting' ? 'warn' : 'bad'}>
            {traffic.connection === 'live' ? 'In ascolto' : traffic.connection === 'connecting' ? 'Connessione…' : 'Offline'}
          </Badge>
          <Button variant="ghost" onClick={() => trafficStore.clear()}>
            Pulisci
          </Button>
        </>
      }
    >
      <Card>
        <FlowDiagram events={shown} />
      </Card>

      <Card
        title={`Eventi (${shown.length})`}
        hint="Finestra sugli ultimi 300 eventi: lo storico completo sta in Messages e in Audit"
      >
        <div className="filters">
          <Dropdown
            label="Canale"
            value={channel}
            onChange={setChannel}
            options={[{ value: '', label: 'Tutti i canali' }, ...channels.map((name) => ({ value: name, label: name }))]}
          />
          <Dropdown
            label="Applicazione"
            value={application}
            onChange={setApplication}
            options={[{ value: '', label: 'Tutte le applicazioni' }, ...applications.map((name) => ({ value: name, label: name }))]}
          />
          <label className="checkbox">
            <input type="checkbox" checked={onlyProblems} onChange={(event) => setOnlyProblems(event.target.checked)} />
            Solo problemi
          </label>
        </div>

        {shown.length === 0 ? (
          <Empty>Nessun evento da mostrare. Appena un&apos;applicazione pubblica, compare qui.</Empty>
        ) : (
          <Table
            head={
              <tr>
                <th>Ora</th>
                <th>Applicazione</th>
                <th>Canale</th>
                <th>Direzione</th>
                <th>Tipo</th>
                <th>Stato</th>
                <th className="right">Elaborazione</th>
                <th>Dettaglio</th>
              </tr>
            }
          >
            {shown.map((event) => (
              <tr key={event.sequence} className={event.type === 'message.dead-lettered' ? 'row-bad' : undefined}>
                <td className="mono">{formatTime(event.occurredAt)}</td>
                <td>{event.direction === 'inbound' ? (event.from ?? '—') : (event.to ?? event.from ?? '—')}</td>
                <td>{event.channel ?? '—'}</td>
                <td>
                  <span className="muted small">{event.direction}</span>
                </td>
                <td>
                  {event.messageId ? (
                    <Link to={`/messages/${event.messageId}`}>{event.messageType ?? event.channel}</Link>
                  ) : (
                    (event.messageType ?? '—')
                  )}
                </td>
                <td>
                  <Badge tone={toneForStatus(event.type.replace('message.', ''))}>{event.type.replace('message.', '')}</Badge>
                </td>
                <td className="right">{formatDuration(event.processingMs)}</td>
                <td className="muted small">{event.detail ?? (event.attempt ? `tentativo ${event.attempt}` : '')}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </Page>
  );
}
